/**

 * Mock BaaS personal account creation — replace with API when backend is ready.

 */



import { baasProxy } from "./baasConsoleClient";

import { createMockBaasEntity } from "./baasEntidadesApi";

import {
  hasIdentityDocNumber,
  isBrCountry,
  isIdentityDocComplete,
} from "../Pages/Private/BaasConsole/Entidades/baasEntidadeCreateVisibility";
import {
  composeE164FromParts,
  createEmptyPhoneParts,
  isPhonePartsComplete,
  mapPhonePartsFromPayload,
  toPhonePartsPayload,
} from "./baasPhoneParts";



const EMPTY_PERSONAL_IDENTITY_DOC = {
  id: "",
  number: "",
  countryCode: "",
  expirationDate: "",
  issuingRegion: "",
};



/** Default sample payload for personal account creation. */

export const DEFAULT_PERSONAL_ACCOUNT_PAYLOAD = {

  first_name: "Maria",

  last_name: "Silva",

  date_of_birth: "1990-05-15",

  mother_name: "Ana Silva",

  email: "maria.silva@example.com",

  phone_number: "+5511999887766",

  address: {

    line_1: "Rua das Flores",

    number: "100",

    neighborhood: "Centro",

    city: "Sao Paulo",

    state: "SP",

    postal_code: "01310-100",

    country_code: "BR",

  },

  national_id: {

    number: "48148129061",

    country_code: "BR",

    expiration_date: null,

    issuing_region: null,

  },

};



/** Document-first: lookup existing person before filling personal data / address. */
export const BAAS_PERSONAL_WIZARD_STEPS = ["document", "personalData", "address", "review"];



const PERSONAL_DRAFT_KEY_PREFIX = "baas-entidade-personal-draft-";



export const getPersonalEntidadeDraftStorageKey = (instanceId) =>

  `${PERSONAL_DRAFT_KEY_PREFIX}${instanceId || "default"}`;



/** @param {string} instanceId @param {{ form: object, stepIndex: number }} draft */

export const savePersonalEntidadeDraft = (instanceId, draft) => {

  if (!instanceId) {

    return;

  }

  try {

    sessionStorage.setItem(

      getPersonalEntidadeDraftStorageKey(instanceId),

      JSON.stringify({ ...draft, savedAt: Date.now() }),

    );

  } catch {

    // sessionStorage may be unavailable or full

  }

};



/** @param {string} instanceId @returns {{ form: object, stepIndex: number } | null} */

export const loadPersonalEntidadeDraft = (instanceId) => {

  if (!instanceId) {

    return null;

  }

  try {

    const raw = sessionStorage.getItem(getPersonalEntidadeDraftStorageKey(instanceId));

    if (!raw) {

      return null;

    }

    const parsed = JSON.parse(raw);

    if (!parsed?.form || typeof parsed.stepIndex !== "number") {

      return null;

    }

    return parsed;

  } catch {

    return null;

  }

};



/** @param {string} instanceId */

export const clearPersonalEntidadeDraft = (instanceId) => {

  if (!instanceId) {

    return;

  }

  try {

    sessionStorage.removeItem(getPersonalEntidadeDraftStorageKey(instanceId));

  } catch {

    // ignore

  }

};



const mapPersonalAddressFromPayload = (address = {}) => ({
  line1: address.line_1 ?? address.line1 ?? "",
  number: address.number ?? "",
  neighborhood: address.neighborhood ?? address.line_2 ?? address.line2 ?? "",
  city: address.city ?? "",
  state: address.state ?? "",
  postalCode: address.postal_code ?? address.postalCode ?? "",
  countryCode: address.country_code ?? address.countryCode ?? "",
});

const mapIdentityDocFromPayload = (doc = {}) => ({
  id: String(doc.id ?? doc.Id ?? "").trim(),
  number: doc.number ?? doc.Number ?? "",
  countryCode: doc.country_code ?? doc.countryCode ?? doc.CountryCode ?? "",
  expirationDate: doc.expiration_date ?? doc.expirationDate ?? doc.ExpirationDate ?? "",
  issuingRegion: doc.issuing_region ?? doc.issuingRegion ?? doc.IssuingRegion ?? "",
});

const mapIdentityDocToPayload = (doc) => ({
  number: doc.number.trim(),
  country_code: doc.countryCode.trim(),
  expiration_date: doc.expirationDate || null,
  issuing_region: doc.issuingRegion.trim() || null,
});

export const hasPersonalIdentityDocNumber = (doc) => Boolean(doc?.number?.trim());

/** At least one identity document must have a number filled. */
export const hasAnyPersonalIdentityDocNumber = (form) =>
  hasPersonalIdentityDocNumber(form.passport) ||
  hasPersonalIdentityDocNumber(form.driversLicense) ||
  hasPersonalIdentityDocNumber(form.nationalId);

/** Initializes editable form state for a new personal entity (empty defaults only). */
export const createInitialPersonalCreateForm = () => ({
  firstName: "",
  lastName: "",
  dateOfBirth: "",
  motherName: "",
  email: "",
  phone: createEmptyPhoneParts(),
  address: {
    line1: "",
    number: "",
    neighborhood: "",
    city: "",
    state: "",
    postalCode: "",
    countryCode: "BR",
  },
  passport: { ...EMPTY_PERSONAL_IDENTITY_DOC },
  driversLicense: { ...EMPTY_PERSONAL_IDENTITY_DOC },
  nationalId: { ...EMPTY_PERSONAL_IDENTITY_DOC, countryCode: "BR" },
});

/**
 * Maps a BaaS GET /v1/entities/{id} person response into personal create-form state (read-only detail).
 * @param {object} entityRaw
 */
export const mapPersonEntityResponseToForm = (entityRaw) => {
  const details = entityRaw?.person_details ?? entityRaw?.personDetails ?? entityRaw ?? {};
  const address = details.address ?? {};
  const base = createInitialPersonalCreateForm();
  const mappedAddress = mapPersonalAddressFromPayload(address);
  const documents = Array.isArray(entityRaw?.documents)
    ? entityRaw.documents
    : Array.isArray(entityRaw?.Documents)
      ? entityRaw.Documents
      : [];

  const findDoc = (type) =>
    documents.find((doc) => {
      const docType = String(
        doc.document_id ?? doc.documentId ?? doc.DocumentId ?? doc.description ?? "",
      ).toUpperCase();
      return docType === type;
    });

  /** Merge detail payload + documents list; always prefer a real PersonDocument id when present. */
  const preferIdentityDoc = (fromDetails, type) => {
    const fromDetailsMapped = mapIdentityDocFromPayload(fromDetails);
    const fromList = findDoc(type);
    const fromListMapped = fromList
      ? mapIdentityDocFromPayload({
          id: fromList.id ?? fromList.Id,
          number: fromList.number ?? fromList.Number,
          country_code: fromList.country_code ?? fromList.countryCode ?? fromList.CountryCode,
          expiration_date:
            fromList.expiration_date ?? fromList.expirationDate ?? fromList.ExpirationDate,
          issuing_region:
            fromList.issuing_region ?? fromList.issuingRegion ?? fromList.IssuingRegion,
        })
      : { ...EMPTY_PERSONAL_IDENTITY_DOC };

    return {
      id: fromListMapped.id || fromDetailsMapped.id || "",
      number: fromDetailsMapped.number?.trim()
        ? fromDetailsMapped.number
        : fromListMapped.number || "",
      countryCode: fromDetailsMapped.countryCode?.trim()
        ? fromDetailsMapped.countryCode
        : fromListMapped.countryCode || "",
      expirationDate: fromDetailsMapped.expirationDate?.trim()
        ? fromDetailsMapped.expirationDate
        : fromListMapped.expirationDate || "",
      issuingRegion: fromDetailsMapped.issuingRegion?.trim()
        ? fromDetailsMapped.issuingRegion
        : fromListMapped.issuingRegion || "",
    };
  };

  return normalizePersonalCreateForm({
    ...base,
    firstName: details.first_name ?? details.firstName ?? "",
    lastName: details.last_name ?? details.lastName ?? "",
    dateOfBirth: details.date_of_birth ?? details.dateOfBirth ?? "",
    motherName: details.mother_name ?? details.motherName ?? "",
    email:
      details.email
      ?? entityRaw?.access_email?.real_email
      ?? entityRaw?.accessEmail?.realEmail
      ?? "",
    phone: mapPhonePartsFromPayload({
      phone: details.phone,
      phone_number: details.phone_number ?? details.phoneNumber,
    }),
    address: {
      ...base.address,
      ...mappedAddress,
      number: mappedAddress.number || base.address.number,
      neighborhood: mappedAddress.neighborhood || base.address.neighborhood,
    },
    passport: preferIdentityDoc(details.passport, "PASSPORT"),
    driversLicense: preferIdentityDoc(
      details.drivers_license ?? details.driversLicense,
      "DRIVERS_LICENSE",
    ),
    nationalId: preferIdentityDoc(details.national_id ?? details.nationalId, "NATIONAL_ID"),
  });
};

/** Merges persisted draft data with the current form defaults. */
export const normalizePersonalCreateForm = (form) => {
  const defaults = createInitialPersonalCreateForm();

  let phoneParts = defaults.phone;
  if (form?.phone && typeof form.phone === "object") {
    phoneParts = { ...defaults.phone, ...form.phone };
  } else if (typeof form?.phone === "string" && form.phone.trim()) {
    phoneParts = mapPhonePartsFromPayload({ phone_number: form.phone });
  } else if (form?.phoneNumber) {
    phoneParts = mapPhonePartsFromPayload({ phone_number: form.phoneNumber });
  }

  return {
    ...defaults,
    ...form,
    phone: phoneParts,
    address: { ...defaults.address, ...(form?.address ?? {}) },
    passport: { ...defaults.passport, ...(form?.passport ?? {}) },
    driversLicense: { ...defaults.driversLicense, ...(form?.driversLicense ?? {}) },
    nationalId: { ...defaults.nationalId, ...(form?.nationalId ?? {}) },
  };
};



/** @returns {string} Pretty-printed default personal account payload JSON. */

export const getDefaultPersonalAccountPayloadJson = () =>

  JSON.stringify(DEFAULT_PERSONAL_ACCOUNT_PAYLOAD, null, 2);



const isIdentityDocObject = (value) => value && typeof value === "object" && !Array.isArray(value);



const payloadHasAnyIdentityDocNumber = (payload) =>

  hasPersonalIdentityDocNumber({

    number: payload.passport?.number,

  }) ||

  hasPersonalIdentityDocNumber({

    number: payload.drivers_license?.number,

  }) ||

  hasPersonalIdentityDocNumber({

    number: payload.national_id?.number,

  });



/**

 * Parses and validates raw JSON for personal account creation.

 * @param {string} jsonText

 */

export const parsePersonalAccountPayloadJson = (jsonText) => {

  const parsed = JSON.parse(jsonText);



  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {

    throw new SyntaxError("Invalid payload");

  }



  if (!parsed.first_name?.trim() || !parsed.last_name?.trim()) {

    throw new SyntaxError("Missing first_name or last_name");

  }



  if (!parsed.address || typeof parsed.address !== "object") {

    throw new SyntaxError("Missing address");

  }



  if (

    (parsed.passport != null && !isIdentityDocObject(parsed.passport)) ||

    (parsed.drivers_license != null && !isIdentityDocObject(parsed.drivers_license)) ||

    (parsed.national_id != null && !isIdentityDocObject(parsed.national_id))

  ) {

    throw new SyntaxError("Invalid passport, drivers_license, or national_id");

  }



  if (!payloadHasAnyIdentityDocNumber(parsed)) {

    throw new SyntaxError("At least one identity document number is required");

  }



  return parsed;

};



const toE164Phone = (phone) => {

  const digits = (phone || "").replace(/\D/g, "");

  if (!digits) {

    return "";

  }

  if (digits.startsWith("55") && digits.length >= 12) {

    return `+${digits}`;

  }

  if (digits.startsWith("1") && digits.length >= 11) {

    return `+${digits}`;

  }

  return `+${digits}`;

};



const mapPersonalAddressToPayload = (address) => ({

  line_1: address.line1.trim(),

  number: address.number.trim(),

  neighborhood: address.neighborhood.trim(),

  city: address.city.trim(),

  state: address.state.trim(),

  postal_code: address.postalCode.trim(),

  country_code: address.countryCode.trim(),

});



/** Maps a filled identity doc; returns null when number is blank so the key can be omitted. */

const mapIdentityDocForPayload = (doc) => {

  const mapped = mapIdentityDocToPayload(doc);

  if (!mapped.number) {

    return null;

  }

  if (isBrCountry(mapped.country_code)) {

    mapped.number = mapped.number.replace(/\D/g, "");

  }

  return mapped.number ? mapped : null;

};



/**

 * Maps UI form values into the personal account payload shape.

 * Omits passport / drivers_license / national_id when the document number is blank.

 * @param {ReturnType<typeof createInitialPersonalCreateForm>} form

 */

export const buildPersonalAccountPayload = (form) => {

  const payload = {

    first_name: form.firstName.trim(),

    last_name: form.lastName.trim(),

    date_of_birth: form.dateOfBirth || null,

    mother_name: form.motherName.trim() || null,

    email: form.email.trim(),

    phone_number: composeE164FromParts(form.phone) || null,

    phone: toPhonePartsPayload(form.phone),

    address: mapPersonalAddressToPayload(form.address),

  };



  const passport = mapIdentityDocForPayload(form.passport);

  const driversLicense = mapIdentityDocForPayload(form.driversLicense);

  const nationalId = mapIdentityDocForPayload(form.nationalId);

  if (passport) payload.passport = passport;

  if (driversLicense) payload.drivers_license = driversLicense;

  if (nationalId) payload.national_id = nationalId;



  return payload;

};



const isPersonalAddressComplete = (address) =>

  Boolean(

    address.line1?.trim() &&

      address.number?.trim() &&

      address.neighborhood?.trim() &&

      address.city?.trim() &&

      address.state?.trim() &&

      address.postalCode?.trim() &&

      address.countryCode?.trim(),

  );



const digitsOnlyPersonalDoc = (value) => String(value ?? "").replace(/\D/g, "");



/**

 * BR CPF checksum. Empty / non-11 returns false.

 * @param {string} value

 */

export const isValidPersonalCpf = (value) => {

  const c = digitsOnlyPersonalDoc(value);

  if (c.length !== 11) return false;

  if (/^(\d)\1{10}$/.test(c)) return false;



  let sum = 0;

  for (let i = 1; i <= 9; i += 1) sum += parseInt(c[i - 1], 10) * (11 - i);

  let rem = (sum * 10) % 11;

  if (rem === 10 || rem === 11) rem = 0;

  if (rem !== parseInt(c[9], 10)) return false;



  sum = 0;

  for (let i = 1; i <= 10; i += 1) sum += parseInt(c[i - 1], 10) * (12 - i);

  rem = (sum * 10) % 11;

  if (rem === 10 || rem === 11) rem = 0;

  return rem === parseInt(c[10], 10);

};



/**

 * Formats CPF/CNPJ digits for display while typing.

 * @param {string} value

 */

export const formatPersonalDocumentInput = (value) => {

  const digits = digitsOnlyPersonalDoc(value).slice(0, 14);

  if (digits.length <= 11) {

    return digits

      .replace(/(\d{3})(\d)/, "$1.$2")

      .replace(/(\d{3})(\d)/, "$1.$2")

      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");

  }

  return digits

    .replace(/^(\d{2})(\d)/, "$1.$2")

    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")

    .replace(/\.(\d{3})(\d)/, ".$1/$2")

    .replace(/(\d{4})(\d)/, "$1-$2");

};



/**

 * Prefills personal create form from GET /v1/ib/baas/users/lookup person payload.

 * @param {ReturnType<typeof createInitialPersonalCreateForm>} form

 * @param {object|null} person

 */

export const applyPersonLookupToPersonalForm = (form, person) => {

  if (!person) {

    return normalizePersonalCreateForm(form);

  }



  const defaults = createInitialPersonalCreateForm();

  const docDigits = digitsOnlyPersonalDoc(

    person.nationalRegistration || form.nationalId?.number || "",

  );

  const phoneFromParts =

    person.phoneCountryDialCode || person.phoneAreaCode || person.phoneLocalNumber

      ? {

          ...defaults.phone,

          countryDialCode: person.phoneCountryDialCode || defaults.phone.countryDialCode,

          areaCode: person.phoneAreaCode || "",

          number: person.phoneLocalNumber || "",

        }

      : person.phoneNumber

        ? mapPhonePartsFromPayload({ phone_number: person.phoneNumber })

        : form.phone;



  const addressFromLookup = person.address

    ? {

        line1: person.address.line1 || form.address.line1,

        number: person.address.number || form.address.number,

        neighborhood: person.address.neighborhood || form.address.neighborhood,

        city: person.address.city || form.address.city,

        state: person.address.state || form.address.state,

        postalCode: person.address.postalCode || form.address.postalCode,

        countryCode: person.address.countryCode || form.address.countryCode || "BR",

      }

    : form.address;



  return normalizePersonalCreateForm({

    ...form,

    firstName: person.name || form.firstName,

    lastName: person.lastName || form.lastName,

    dateOfBirth: (person.dateOfBirth || form.dateOfBirth || "").slice(0, 10),

    motherName: person.motherName || form.motherName,

    email: person.email || form.email,

    phone: phoneFromParts,

    address: addressFromLookup,

    nationalId: {

      ...form.nationalId,

      number: docDigits

        ? formatPersonalDocumentInput(docDigits)

        : form.nationalId.number,

      countryCode: form.nationalId.countryCode || "BR",

    },

  });

};



/**

 * Prefills personal create form from POST /v1/entities/autocomplete (CPF) draft.

 * @param {ReturnType<typeof createInitialPersonalCreateForm>} form

 * @param {object|null} draft snake_case person fields from API

 */

export const applySerproPersonDraftToPersonalForm = (form, draft) => {

  if (!draft) {

    return normalizePersonalCreateForm(form);

  }



  const docDigits = digitsOnlyPersonalDoc(

    draft.national_id?.number || draft.nationalId?.number || form.nationalId?.number || "",

  );



  return normalizePersonalCreateForm({

    ...form,

    firstName: draft.first_name || draft.firstName || form.firstName,

    lastName: draft.last_name || draft.lastName || form.lastName,

    dateOfBirth: String(draft.date_of_birth || draft.dateOfBirth || form.dateOfBirth || "").slice(

      0,

      10,

    ),

    nationalId: {

      ...form.nationalId,

      number: docDigits

        ? formatPersonalDocumentInput(docDigits)

        : form.nationalId.number,

      countryCode:

        draft.national_id?.country_code

        || draft.nationalId?.countryCode

        || form.nationalId.countryCode

        || "BR",

    },

    countryOfCitizenship:

      draft.country_of_citizenship || draft.countryOfCitizenship || form.countryOfCitizenship || "BR",

  });

};



/**

 * Digits for person lookup (CPF/CNPJ) from the form's primary document.

 * @param {ReturnType<typeof createInitialPersonalCreateForm>} form

 */

export const getPersonalLookupDocumentDigits = (form) => {

  const national = digitsOnlyPersonalDoc(form?.nationalId?.number);

  if (national.length === 11 || national.length === 14) {

    return national;

  }

  const passport = String(form?.passport?.number ?? "").trim();

  if (passport) {

    return passport.replace(/\W/g, "");

  }

  return digitsOnlyPersonalDoc(form?.driversLicense?.number);

};



/**

 * Validates a single wizard step.

 * @returns {{ valid: boolean, errorKey?: string }}

 */

export const validatePersonalFormStep = (step, form, { instanceId = "" } = {}) => {

  switch (step) {

    case "document":

      if (!instanceId) {

        return { valid: false, errorKey: "baas.entidades.personal.instanceRequired" };

      }

      if (!hasAnyPersonalIdentityDocNumber(form)) {

        return { valid: false, errorKey: "baas.entidades.personal.documentRequired" };

      }

      {

        const filledDocs = [form.passport, form.driversLicense, form.nationalId].filter(

          hasIdentityDocNumber,

        );

        if (filledDocs.some((doc) => !isIdentityDocComplete(doc))) {

          return { valid: false, errorKey: "baas.entidades.personal.identityDocIncomplete" };

        }

        const nationalDigits = String(form.nationalId?.number ?? "").replace(/\D/g, "");

        if (

          nationalDigits &&

          isBrCountry(form.nationalId?.countryCode || "BR") &&

          nationalDigits.length === 11 &&

          !isValidPersonalCpf(nationalDigits)

        ) {

          return { valid: false, errorKey: "baas.entidades.personal.cpfInvalid" };

        }

      }

      break;



    case "personalData":

      if (

        !form.firstName.trim() ||

        !form.lastName.trim() ||

        !form.dateOfBirth ||

        !form.motherName.trim() ||

        !form.email.trim() ||

        !isPhonePartsComplete(form.phone)

      ) {

        return { valid: false, errorKey: "baas.entidades.personal.personalDataRequired" };

      }

      break;



    case "address":

      if (!isPersonalAddressComplete(form.address)) {

        return { valid: false, errorKey: "baas.entidades.personal.addressRequired" };

      }

      break;



    default:

      break;

  }



  return { valid: true };

};



/**

 * Returns per-step completion for wizard tab indicators.

 * @returns {'complete' | 'pending'}

 */

export const getPersonalStepCompletionStatus = (step, form, { instanceId = "" } = {}) => {

  if (step === "review") {

    const hasPending = BAAS_PERSONAL_WIZARD_STEPS.some((wizardStep) => {

      if (wizardStep === "review") {

        return false;

      }

      return (

        getPersonalStepCompletionStatus(wizardStep, form, { instanceId }) === "pending"

      );

    });

    return hasPending ? "pending" : "complete";

  }



  const result = validatePersonalFormStep(step, form, { instanceId });

  return result.valid ? "complete" : "pending";

};



/**

 * Validates all required fields for submit.

 * @returns {{ valid: boolean, errorKey?: string }}

 */

export const validatePersonalFormForSubmit = (form, { instanceId = "" } = {}) => {
  const issues = [];
  const seen = new Set();

  for (const step of BAAS_PERSONAL_WIZARD_STEPS) {
    if (step === "review") {
      continue;
    }
    const result = validatePersonalFormStep(step, form, { instanceId });
    if (!result.valid) {
      const errorKey = result.errorKey || "baas.entidades.personal.incompleteRequiredFields";
      if (!seen.has(errorKey)) {
        seen.add(errorKey);
        issues.push({ step, errorKey });
      }
    }
  }

  return {
    valid: issues.length === 0,
    errorKey: issues[0]?.errorKey,
    issues,
  };
};



/**
 * Validates editable contact fields on entity detail (phone + address only).
 * @returns {{ valid: boolean, errorKey?: string }}
 */
export const validatePersonalContactForEdit = (form) => {
  if (!isPhonePartsComplete(form?.phone)) {
    return { valid: false, errorKey: "baas.entidades.personal.phoneRequired" };
  }
  const phone = composeE164FromParts(form.phone);
  if (!/^\+[1-9]\d{7,14}$/.test(phone)) {
    return { valid: false, errorKey: "baas.entidades.personal.phoneInvalid" };
  }
  if (!isPersonalAddressComplete(form.address)) {
    return { valid: false, errorKey: "baas.entidades.personal.addressRequired" };
  }
  return { valid: true };
};

export const toPersonalE164Phone = (phoneOrParts) => {
  if (phoneOrParts && typeof phoneOrParts === "object") {
    return composeE164FromParts(phoneOrParts);
  }
  return toE164Phone(phoneOrParts);
};

export {
  composeE164FromParts,
  formatPhonePartsForDisplay,
  isPhonePartsComplete,
  toPhonePartsPayload,
} from "./baasPhoneParts";



const formatCpfDocument = (number) => {

  const digits = (number || "").replace(/\D/g, "");

  if (digits.length === 11) {

    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;

  }

  return number;

};



const getPrimaryDocumentNumber = (payload) =>

  payload.national_id?.number?.trim() ||

  payload.passport?.number?.trim() ||

  payload.drivers_license?.number?.trim() ||

  "";



/** Masks a document number for review display, keeping the last 4 characters visible. */

export const maskPersonalDocumentNumber = (number) => {

  const trimmed = (number || "").trim();

  if (!trimmed) {

    return "";

  }

  if (trimmed.length <= 4) {

    return "****";

  }

  return `${"*".repeat(trimmed.length - 4)}${trimmed.slice(-4)}`;

};



/**

 * Creates a personal account (mock) and registers a pending PF entity.

 * @param {string} instanceId

 * @param {typeof DEFAULT_PERSONAL_ACCOUNT_PAYLOAD} payload

 */

export const createPersonalAccount = async (instanceOrId, payload) => {

  const instance = typeof instanceOrId === "object" && instanceOrId != null

    ? instanceOrId

    : null;

  if (!instance?.baasInstanceId) {

    throw new Error("createPersonalAccount requires the selected BaaS instance object");

  }

  const created = await baasProxy({

    instance,

    method: "POST",

    baasPath: "v1/entities/person",

    body: payload,

  });

  const fullName = `${payload.first_name || ""} ${payload.last_name || ""}`.trim() || created?.name || created?.id;

  const primaryDocument = getPrimaryDocumentNumber(payload);

  const entity = createMockBaasEntity(instance.id, {

    id: created?.id,

    name: fullName,

    document: formatCpfDocument(primaryDocument),

    type: "pf",

    status: "pending",

    kybStatus: "pending",

    personalAccount: payload,

  });

  return { entity: { ...entity, id: created?.id || entity.id }, payload, raw: created };

};


